import { MutationKey, QueryKey } from '@tanstack/react-query'
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ASTNode, print } from 'graphql'
import { t } from 'i18next'
import { isEmpty } from 'lodash'

import { getTokenCookies, refreshToken, RefreshTokenResult } from 'features/user'
import { enqueueSnackbarForError500, ServerError } from './errors'
import { GraphQlResponse } from './response'
import { FileItem } from './types'

const client = axios.create({
  baseURL: import.meta.env.VITE_API,
  headers: {
    'Content-Type': 'application/json',
  },
  method: 'post',
})

client.interceptors.request.use(config => {
  const { accessToken } = getTokenCookies()
  config.headers.Authorization = accessToken ? `Bearer ${accessToken}` : ''

  return config
})

let isRefreshing: boolean = false
type QueueItem = (accessToken: string) => void
let queue: QueueItem[] = []

const addToQueue = (
  originalRequest: AxiosRequestConfig,
): Promise<AxiosResponse<GraphQlResponse>> => {
  return new Promise(resolve => {
    queue.push((accessToken: string): void => {
      resolve(
        client({
          ...originalRequest,
          headers: {
            ...originalRequest.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        }),
      )
    })
  })
}

client.interceptors.response.use(
  async (response: AxiosResponse<GraphQlResponse>): Promise<AxiosResponse<GraphQlResponse>> => {
    const isUnauthorized = Boolean(
      response.data.errors?.some((err: ServerError) => err.extensions?.code === 'UNAUTHORIZED'),
    )
    if (!isUnauthorized) {
      return response
    }

    const originalRequest: AxiosRequestConfig<GraphQlResponse> = response.config
    if (isRefreshing) {
      return addToQueue(originalRequest)
    }

    isRefreshing = true
    try {
      const refreshTokenResult: RefreshTokenResult = await refreshToken()
      if (refreshTokenResult?.accessToken === undefined) {
        return response
      }

      const { accessToken } = refreshTokenResult
      // since it's all async, we should immediately stop before creating the queue to minimize side effects
      isRefreshing = false

      queue.forEach((queuedRequest: QueueItem): void => queuedRequest(accessToken))
      queue = []

      return client({
        ...originalRequest,
        headers: {
          ...originalRequest.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      })
    } catch {
      isRefreshing = false
      queue = []
      throw Error(t('error.unableToRefreshToken', { ns: 'providers/tanstackQuery' }))
    }
  },
)

const handleGraphqlMultiPartRequest = (
  input: Record<string, unknown>,
  axiosConfig: AxiosRequestConfig,
): void => {
  const files = Object.entries(input)
    .map(([key, value]: [string, unknown]): FileItem | undefined => {
      if (value instanceof File) {
        return {
          file: value,
          path: `variables.input.${key}`,
        }
      } else {
        return undefined
      }
    })
    .filter(el => el !== undefined)

  if (files.length === 0) {
    return
  }

  axiosConfig.headers = {
    ...axiosConfig.headers,
    'Content-Type': 'multipart/form-data',
  }

  const formData = new FormData()
  formData.append('operations', JSON.stringify(axiosConfig.data))
  formData.append(
    'map',
    JSON.stringify(Object.fromEntries(files.map(({ path }, index) => [index, [path]]))),
  )
  files.forEach(({ file }: FileItem, index: number): void => {
    formData.append(index.toString(), file)
  })

  axiosConfig.data = formData
}

export const request = async <
  Variables extends Record<string, unknown>,
  Data extends GraphQlResponse<unknown>,
>({
  key,
  query,
  variables,
}: {
  key: MutationKey | QueryKey
  query: ASTNode
  variables?: Variables
}): Promise<GraphQlResponse<Data> | void> => {
  try {
    const axiosConfig: AxiosRequestConfig = {
      data: {
        key,
        query: print(query),
        variables: variables ?? undefined,
      },
    }

    if (variables?.input !== undefined && !isEmpty(variables.input)) {
      handleGraphqlMultiPartRequest(variables.input, axiosConfig)
    }

    const { data } = await client(axiosConfig)

    return data
  } catch (error) {
    enqueueSnackbarForError500((error as Error).message)
  }
}
